import { useEffect, useRef } from 'react'
import { Group, Button, Text, Tooltip } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconRefresh } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import { useOrgSyncStatus, useOrgSyncTrigger } from '@/hooks/useOrgSync'

function humanizeAgo(iso: string | null | undefined): string {
  if (!iso) return 'nunca sincronizado'
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 0) return 'agora'
  const min = Math.floor(ms / 60_000)
  if (min < 1) return 'há menos de 1 min'
  if (min < 60) return `há ${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `há ${h} h`
  const d = Math.floor(h / 24)
  return `há ${d} d`
}

export function OrgSyncButton() {
  const { data, isLoading } = useOrgSyncStatus()
  const trigger = useOrgSyncTrigger()
  const qc = useQueryClient()

  const status = data?.last_sync?.status ?? null
  const isRunning = status === 'running'
  // Rastreia a transição de status para notificar a CONCLUSÃO do sync que
  // roda em segundo plano (running → success/error/interrupted).
  const prevStatus = useRef<string | null>(null)

  useEffect(() => {
    const prev = prevStatus.current
    if (prev === 'running' && status && status !== 'running') {
      if (status === 'success') {
        notifications.show({
          color: 'teal',
          title: 'Sincronização concluída',
          message: 'Tenants atualizados da AWS Organizations.',
        })
        // Agora sim os dados estão prontos — recarrega listagens dependentes.
        qc.invalidateQueries({ queryKey: ['tenants'] })
        qc.invalidateQueries({ queryKey: ['aws-ou-tree'] })
      } else {
        notifications.show({
          color: 'red',
          title: 'Sincronização não concluída',
          message:
            status === 'interrupted'
              ? 'A sincronização foi interrompida. Tente novamente.'
              : 'A sincronização terminou com erro. Verifique os logs.',
        })
      }
    }
    prevStatus.current = status
  }, [status, qc])

  // Última sync bem-sucedida: usa finished_at; em andamento mostra "em andamento".
  const lastSync =
    data?.last_sync?.finished_at ?? data?.last_sync?.started_at ?? null
  const tooltipLabel = data?.last_sync?.triggered_by
    ? `Disparado por: ${data.last_sync.triggered_by}`
    : 'Sincronização AWS Organizations'

  const handleClick = () => {
    trigger.mutate(undefined, {
      onSuccess: (result) => {
        if (result?.status === 'already_running') {
          notifications.show({
            color: 'blue',
            title: 'Sincronização em andamento',
            message: 'Já existe uma sincronização rodando. Aguarde a conclusão.',
          })
        } else {
          notifications.show({
            color: 'blue',
            title: 'Sincronização iniciada',
            message:
              'Rodando em segundo plano (leva alguns minutos). O status será atualizado automaticamente.',
          })
        }
      },
      onError: (err: unknown) => {
        notifications.show({
          color: 'red',
          title: 'Erro ao iniciar sincronização',
          message: err instanceof Error ? err.message : String(err),
        })
      },
    })
  }

  return (
    <Group gap="xs" align="center">
      {!isLoading && (
        <Text size="xs" c="dimmed">
          {isRunning ? 'Sincronizando…' : `Última sync: ${humanizeAgo(lastSync)}`}
        </Text>
      )}
      <Tooltip label={tooltipLabel} withArrow>
        <Button
          variant="light"
          size="xs"
          leftSection={<IconRefresh size={14} />}
          loading={trigger.isPending || isRunning}
          disabled={isRunning}
          onClick={handleClick}
        >
          Sincronizar
        </Button>
      </Tooltip>
    </Group>
  )
}
